import React, {
  useState,
  useEffect,
  useRef,
  useMemo,
  forwardRef,
  useImperativeHandle,
} from "react";
import DatePicker from "react-datepicker";
import "./NF2.css";
import {
  GetCaseFormListData,
  SaveCaseFormData,
} from "../medicalLien/services3";
import SignaturePad from "../../../Component/shared/signaturePad";
import { IntakeSheetSession, RoleType } from "../../../helpers/constant";
import SpinnerLoader from "../../../Component/shared/loader/spinnerLoader/SpinnerLoader";
import toast from "react-hot-toast";

// ─── Radio Component ──────────────────────────────────────────────────────────
const Radio = ({ checked, value, name, label, onChange }) => {
  return (
    <label className="nf2__chk-row" style={{ cursor: "pointer" }}>
      <input
        type="radio"
        name={name}
        value={value}
        checked={checked}
        onChange={onChange}
        style={{ marginRight: 6 }}
      />
      {label}
    </label>
  );
};

const SectionBadge = ({ num, title, first }) => (
  <div
    className={`nf2__section${first ? " nf2__section--first" : ""}`}
    style={{ marginTop: first ? 0 : 14 }}
  >
    <span className="nf2__section-title">
      <span className="nf2__section-badge">{num}</span> — {title}
    </span>
    <span className="nf2__section-line" />
  </div>
);

const NF2 = forwardRef(function NF2(props, ref) {
  const {
    selectedRow,
    intakeSheet,
    providerOptions = [],
    sessionData = {},
    signatureMiss,
  } = props;

  // ── tokenData memoized once ──────────────────────────────────────────────────
  const tokenData = useMemo(
    () => JSON.parse(sessionStorage.getItem("tokenData") || "{}"),
    []
  );

  // ─── State ───────────────────────────────────────────────────────────────────
  const [loading, setLoading] = useState(false);
  const [sigPadOpen, setSigPadOpen] = useState(false);
  const [signRole, setSignRole] = useState("");
  const [patientSignDate, setPatientSignDate] = useState(new Date());

  // Insurer / Policy
  const [insurerName, setInsurerName] = useState("");
  const [policyHolder, setPolicyHolder] = useState("");
  const [insureAddress, setInsureAddress] = useState("");
  const [policyNumber, setPolicyNumber] = useState("");
  const [claimNumber, setClaimNumber] = useState("");
  const [insureClaimRepresentive, setInsureClaimRepresentive] = useState("");

  // Applicant / Accident Info
  const [applicantAccidentName, setApplicantAccidentName] = useState(
    selectedRow?.data?.Name ?? selectedRow?.searchResult?.Patient?.Name ?? ""
  );
  const [applicantAccidentPhoneNumber, setApplicantAccidentPhoneNumber] = useState("");
  const [applicantAccidentHomePhoneNo, setApplicantAccidentHomePhoneNo] = useState("");
  const [applicantAccidentBusinessPhoneNumber, setApplicantAccidentBusinessPhoneNumber] = useState("");
  const [applicantAccidentSSN, setApplicantAccidentSSN] = useState("");
  const [applicantAccidentDOB, setApplicantAccidentDOB] = useState(null);
  const [applicantAccidentDOA, setApplicantAccidentDOA] = useState(null);
  const [applicantAccidentAddress, setApplicantAccidentAddress] = useState("");
  const [applicantAccidentAddress2, setApplicantAccidentAddress2] = useState("");
  const [applicantAccidentDesc, setApplicantAccidentDesc] = useState("");
  const [applicantAccidentDescInjury, setApplicantAccidentDescInjury] = useState("");

  // Vehicle Info
  const [ownerName, setOwnerName] = useState("");
  const [make, setMake] = useState("");
  const [year, setYear] = useState("");
  const [vehicleType, setVehicleType] = useState("");
  const [isDriver, setIsDriver] = useState(null);
  const [isPassenger, setIsPassenger] = useState(null);
  const [isPedestrian, setIsPedestrian] = useState(null);
  const [isPolicyHolderHouseHold, setIsPolicyHolderHouseHold] = useState(null);
  const [isOwnMotorVechile, setIsOwnMotorVechile] = useState(null);

  // Treatment Info
  const [treatedByDoctorOrOtherPerson, setTreatedByDoctorOrOtherPerson] = useState(null);
  const [nameAndAddressOfDoctor, setNameAndAddressOfDoctor] = useState("");
  const [hospitalType, setHospitalType] = useState(null);
  const [DOA, setDOA] = useState(null);
  const [hospitalNameAndAddress, setHospitalNameAndAddress] = useState("");
  const [amountOfHealthBillToDate, setAmountOfHealthBillToDate] = useState("");
  const [willHaveMoreTreatment, setWillHaveMoreTreatment] = useState(null);
  const [inCourseOfEmployment, setInCourseOfEmployment] = useState(null);

  // Work Info
  const [lostTimeFromWork, setLostTimeFromWork] = useState("");
  const [dateAbsenceFromWorkBegan, setDateAbsenceFromWorkBegan] = useState(null);
  const [returnToWork, setReturnToWork] = useState("");
  const [dateReturnToWork, setDateReturnToWork] = useState(null);
  const [timeLostFromWork, setTimeLostFromWork] = useState("");
  const [grossAvgWeeklyEarning, setGrossAvgWeeklyEarning] = useState("");
  const [workPerWeek, setWorkPerWeek] = useState("");
  const [workPerDay, setWorkPerDay] = useState("");
  const [receivingUnemploymentBenefit, setReceivingUnemploymentBenefit] = useState(null);

  // Employers
  const [employerAndAddress1, setEmployerAndAddress1] = useState("");
  const [occupation1, setOccupation1] = useState("");
  const [fromDate1, setFromDate1] = useState(null);
  const [toDate1, setToDate1] = useState(null);
  const [employerAndAddress2, setEmployerAndAddress2] = useState("");
  const [occupation2, setOccupation2] = useState("");
  const [fromDate2, setFromDate2] = useState(null);
  const [toDate2, setToDate2] = useState(null);
  const [employerAndAddress3, setEmployerAndAddress3] = useState("");
  const [occupation3, setOccupation3] = useState("");
  const [fromDate3, setFromDate3] = useState(null);
  const [toDate3, setToDate3] = useState(null);

  // Other Benefits
  const [workerCompensation, setWorkerCompensation] = useState(null);
  const [newYorkDisability, setNewYorkDisability] = useState(null);
  const [hasOtherExpenses, setHasOtherExpenses] = useState(null);

  // Signature
  const [patientSignImg, setPatientSignImg] = useState("");
  const [signDate, setSignDate] = useState(new Date());
  const [formSubmissionDate, setFormSubmissionDate] = useState(new Date());

  // ─── stateRef: always holds the latest values — solves ALL stale-closure bugs ─
  const stateRef = useRef({});
  useEffect(() => {
    stateRef.current = {
      insurerName,
      insureAddress,
      policyHolder,
      policyNumber,
      claimNumber,
      insureClaimRepresentive,
      applicantAccidentName,
      applicantAccidentPhoneNumber,
      applicantAccidentHomePhoneNo,
      applicantAccidentBusinessPhoneNumber,
      applicantAccidentSSN,
      applicantAccidentDOB,
      applicantAccidentDOA,
      applicantAccidentAddress,
      applicantAccidentAddress2,
      applicantAccidentDesc,
      applicantAccidentDescInjury,
      signDate,
      patientSignImg,
      ownerName,
      make,
      year,
      vehicleType,
      isDriver,
      isPassenger,
      isPedestrian,
      isPolicyHolderHouseHold,
      isOwnMotorVechile,
      treatedByDoctorOrOtherPerson,
      nameAndAddressOfDoctor,
      hospitalType,
      hospitalNameAndAddress,
      DOA,
      amountOfHealthBillToDate,
      willHaveMoreTreatment,
      inCourseOfEmployment,
      lostTimeFromWork,
      dateAbsenceFromWorkBegan,
      returnToWork,
      dateReturnToWork,
      timeLostFromWork,
      grossAvgWeeklyEarning,
      workPerWeek,
      workPerDay,
      receivingUnemploymentBenefit,
      employerAndAddress1,
      occupation1,
      fromDate1,
      toDate1,
      employerAndAddress2,
      occupation2,
      fromDate2,
      toDate2,
      employerAndAddress3,
      occupation3,
      fromDate3,
      toDate3,
      hasOtherExpenses,
      newYorkDisability,
      workerCompensation,
      formSubmissionDate,
    };
  }); // no dependency array — runs after every render to stay in sync

  // ─── useImperativeHandle — stable, reads from stateRef ───────────────────────
  useImperativeHandle(ref, () => ({
    save: () => saveFunction(),
    isPending: () => !stateRef.current.patientSignImg,
    getSignature: () => stateRef.current.patientSignImg || "",
    applySignature: (sig) => {
      if (!sig) return;
      setPatientSignImg(sig);
      setPatientSignDate(new Date());
    },
  })); // no dep array needed — ref methods read live state via stateRef

  useEffect(() => {
    props?.onReady?.("nf2");
  }, [patientSignImg]);

  // ─── Helpers ─────────────────────────────────────────────────────────────────
  const handleYesNo = (setter) => (e) => {
    setter(e.target.value === "yes");
  };

  const formatDate = (date) => {
    if (!date) return "";
    const d = new Date(date);
    if (isNaN(d.getTime())) return "";
    return d.toLocaleDateString("en-US", {
      month: "2-digit",
      day: "2-digit",
      year: "numeric",
    });
  };

  const safeDate = (val) => {
    if (!val) return null;
    if (val.includes("/")) {
      const [month, day, year] = val.split("/");
      const d = new Date(
        `${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`
      );
      return isNaN(d.getTime()) ? null : d;
    }
    const d = new Date(val);
    return isNaN(d.getTime()) ? null : d;
  };

  const normalizeSignatureDataUrl = (sig) => {
    if (!sig) return "";
    if (typeof sig !== "string") return "";
    const trimmed = sig.trim();
    if (!trimmed) return "";
    if (trimmed.startsWith("data:")) return trimmed;
    return `${IntakeSheetSession.dataBlock}${trimmed}`;
  };

  const clickPatientSign = () => {
    setSignRole(RoleType.Patient);
    setSigPadOpen(true);
  };

  const signUpdatedCall = (base64Img) => {
    const newBase = base64Img.includes(IntakeSheetSession.dataBlock1)
      ? base64Img.replace(IntakeSheetSession.dataBlock1, IntakeSheetSession.dataBlock)
      : base64Img;
    if (signRole === RoleType.Patient) {
      setPatientSignImg(newBase);
      setPatientSignDate(new Date());
      sessionStorage.setItem("sharedPatientSignature", newBase);
    }
  };

  const clearSignCall = () => {
    if (signRole === RoleType.Patient) {
      setPatientSignImg("");
      setPatientSignDate("");
      signatureMiss?.(true);
    }
  };

  // ─── Fetch form data ──────────────────────────────────────────────────────────
  const handleGetFormData = async () => {
    try {
      setLoading(true);
      const payload = {
        CaseFormDataId: null,
        CaseId: tokenData?.caseId ?? 0,
        CompanyId: tokenData?.companyId ?? "",
        UserId: tokenData?.userId ?? "US000000000000012172",
        DoctorId: tokenData?.doctorId ?? "",
        OfficeId: tokenData?.providerId ?? "",
        OldCaseFormDataId: 0,
        IsCopySign: false,
        ProviderSign: "",
        IsChanged: false,
        SavePdfInDocMgr: false,
        FormId: 4,
        FormName: "NF 2",
        TemplateName: "",
        EntityLevel: 0,
        FormType: "",
        IsFormCompleted: false,
        JsonData: "",
        FileName: "",
        FilePath: "",
      };

      const response = await GetCaseFormListData(payload);

      if (!response?.Data?.[0]?.JsonData) {
        console.warn("No JsonData found in response");
        return;
      }

      const parsed = JSON.parse(response.Data[0].JsonData);
      const notesdata = parsed.notesdata;

      const getVal = (sectionName, fieldName) => {
        const section = notesdata.find((s) => s.RenderKeyName === sectionName);
        return (
          section?.ApplicationValue?.find((f) => f.Name === fieldName)?.Value ?? ""
        );
      };

      const getBool = (sectionName, yesField, noField) => {
        const yesVal = getVal(sectionName, yesField);
        if (yesVal === true) return true;
        if (yesVal === false) return false;
        const noVal = getVal(sectionName, noField);
        if (noVal === true) return false;
        if (noVal === false) return true;
        return null;
      };

      // ── 1. Insurance Info ────────────────────────────────────────────────────
      setInsurerName(getVal("InsuranceInfo", "InsurerName"));
      setInsureAddress(getVal("InsuranceInfo", "InsurerAddress"));
      setPolicyHolder(getVal("InsuranceInfo", "PolicyHolder"));
      setPolicyNumber(getVal("InsuranceInfo", "PolicyNumber"));
      setClaimNumber(getVal("InsuranceInfo", "ClaimNumber"));
      setInsureClaimRepresentive(getVal("InsuranceInfo", "RepresentativeInfo"));

      // ── 2. Applicant Info ────────────────────────────────────────────────────
      setApplicantAccidentName(getVal("ApplicantInfo", "AIName"));
      setApplicantAccidentPhoneNumber(getVal("ApplicantInfo", "AIPhoneNo"));
      setApplicantAccidentHomePhoneNo(getVal("ApplicantInfo", "AIHomePhNo"));
      setApplicantAccidentBusinessPhoneNumber(getVal("ApplicantInfo", "AIBussPhNo"));
      setApplicantAccidentSSN(getVal("ApplicantInfo", "AISSN"));
      setApplicantAccidentDOB(safeDate(getVal("ApplicantInfo", "AIDOB")));
      setApplicantAccidentDOA(safeDate(getVal("ApplicantInfo", "AIDOA")));
      setApplicantAccidentAddress(getVal("ApplicantInfo", "AIAddress"));
      setApplicantAccidentAddress2(getVal("ApplicantInfo", "AIAccPlaceInfo"));
      setApplicantAccidentDesc(getVal("ApplicantInfo", "AIAccDesc"));
      setApplicantAccidentDescInjury(getVal("ApplicantInfo", "AIInjuryDesc"));
      setSignDate(safeDate(getVal("ApplicantInfo", "AISignDate")) ?? new Date());

      // ── 3. Sign Info ─────────────────────────────────────────────────────────
      const savedSig = props.copySignature
        ? sessionStorage.getItem("sharedPatientSignature")
        : null;
      setPatientSignImg(savedSig || getVal("SignInfo", "AISign"));

      // ── 4. Vehicle Info ──────────────────────────────────────────────────────
      setOwnerName(getVal("VehicleInfo", "VIOwnerName"));
      setMake(getVal("VehicleInfo", "VIMake"));
      setYear(getVal("VehicleInfo", "VIYear"));

      const vehicleTypeMap = {
        VITypeBus: "bus",
        VITypeTruck: "truck",
        VITypeAutomobile: "auto",
        VITypeMotorcycle: "motorcycle",
      };
      const viValues =
        notesdata.find((s) => s.RenderKeyName === "VehicleInfo")?.ApplicationValue || [];
      const selectedVehicle = Object.keys(vehicleTypeMap).find((key) =>
        viValues.some((v) => v.Name === key && v.Value === true)
      );
      setVehicleType(vehicleTypeMap[selectedVehicle] || "");

      setIsDriver(getBool("VehicleInfo", "VIDriverYes", "VIDriverNo"));
      setIsPassenger(getBool("VehicleInfo", "VIPassengerYes", "VIPassengerNo"));
      setIsPedestrian(getBool("VehicleInfo", "VIPedestrianYes", "VIPedestrianNo"));
      setIsPolicyHolderHouseHold(getBool("VehicleInfo", "VIPolicyMbrYes", "VIPolicyMbrNo"));
      setIsOwnMotorVechile(getBool("VehicleInfo", "VIRelativeYes", "VIRelativeNo"));

      // ── 5. Treatment Info ────────────────────────────────────────────────────
      const hospitalTypeMap = { TIOutPatientYes: "out", TIInPatientYes: "in" };
      const treatmentValues =
        notesdata.find((s) => s.RenderKeyName === "TreatmentInfo")?.ApplicationValue || [];
      const selectedHospitalKey = Object.keys(hospitalTypeMap).find((key) =>
        treatmentValues.some((field) => field.Name === key && field.Value === true)
      );
      setHospitalType(selectedHospitalKey ? hospitalTypeMap[selectedHospitalKey] : "");

      setTreatedByDoctorOrOtherPerson(getBool("TreatmentInfo", "TIByDoctorYes", "TIByDoctorNo"));
      setNameAndAddressOfDoctor(getVal("TreatmentInfo", "TIDoctorInfo"));
      setHospitalNameAndAddress(getVal("TreatmentInfo", "TIHospitalInfo"));
      setDOA(safeDate(getVal("TreatmentInfo", "TIAddmitDate")));
      setAmountOfHealthBillToDate(getVal("TreatmentInfo", "TIBillAmtToDate"));
      setWillHaveMoreTreatment(getBool("TreatmentInfo", "TIMoreTreatmentYes", "TIMoreTreatmentNo"));
      setInCourseOfEmployment(getBool("TreatmentInfo", "TICourseEmpYes", "TICourseEmpNo"));

      // ── 6. Work Info ─────────────────────────────────────────────────────────
      setLostTimeFromWork(getVal("WorkInfo", "WILoseTime"));
      setDateAbsenceFromWorkBegan(safeDate(getVal("WorkInfo", "WIAbsenceDate")));
      setReturnToWork(getVal("WorkInfo", "WIReturnWork"));
      setDateReturnToWork(safeDate(getVal("WorkInfo", "WIReturnDate")));
      setTimeLostFromWork(getVal("WorkInfo", "WITimeLost"));
      setGrossAvgWeeklyEarning(getVal("WorkInfo", "WIWeeklyEarning"));
      setWorkPerWeek(getVal("WorkInfo", "WINoDayWeek"));
      setWorkPerDay(getVal("WorkInfo", "WINoHrsDay"));
      setReceivingUnemploymentBenefit(getBool("WorkInfo", "WIUnempBenefitYes", "WIUnempBenefitNo"));

      // ── 7. Employer Info ─────────────────────────────────────────────────────
      setEmployerAndAddress1(getVal("EmployeerInfo", "EI1Address"));
      setOccupation1(getVal("EmployeerInfo", "EI1Occupation"));
      setFromDate1(safeDate(getVal("EmployeerInfo", "EI1FromDate")));
      setToDate1(safeDate(getVal("EmployeerInfo", "EI1ToDate")));
      setEmployerAndAddress2(getVal("EmployeerInfo", "EI2Address"));
      setOccupation2(getVal("EmployeerInfo", "EI2Occupation"));
      setFromDate2(safeDate(getVal("EmployeerInfo", "EI2FromDate")));
      setToDate2(safeDate(getVal("EmployeerInfo", "EI2ToDate")));
      setEmployerAndAddress3(getVal("EmployeerInfo", "EI3Address"));
      setOccupation3(getVal("EmployeerInfo", "EI3Occupation"));
      setFromDate3(safeDate(getVal("EmployeerInfo", "EI3FromDate")));
      setToDate3(safeDate(getVal("EmployeerInfo", "EI3ToDate")));

      // ── 8. Other Info ────────────────────────────────────────────────────────
      setHasOtherExpenses(getBool("OtherInfo", "OIOtherExpYes", "OIOtherExpNo"));
      setNewYorkDisability(getBool("OtherInfo", "OINYStDisabYes", "OINYStDisabNo"));
      setWorkerCompensation(getBool("OtherInfo", "OIWCYes", "OIWCNo"));
      setFormSubmissionDate(safeDate(getVal("OtherInfo", "OIFormDate")) ?? new Date());
    } catch (error) {
      console.error("Error fetching form data:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    handleGetFormData();
  }, []);

  // ─── Build payload from stateRef — always fresh, no stale closures ────────────
  const buildNotesData = () => {
    const s = stateRef.current;
    return [
      {
        RenderKeyName: "InsuranceInfo",
        ApplicationValue: [
          { isRender: true, Type: "Text", Name: "InsurerName",       Value: s.insurerName || "" },
          { isRender: true, Type: "Text", Name: "InsurerAddress",    Value: s.insureAddress || "" },
          { isRender: true, Type: "Text", Name: "PolicyHolder",      Value: s.policyHolder || "" },
          { isRender: true, Type: "Text", Name: "PolicyNumber",      Value: s.policyNumber || "" },
          { isRender: true, Type: "Text", Name: "ClaimNumber",       Value: s.claimNumber || "" },
          { isRender: true, Type: "Text", Name: "RepresentativeInfo",Value: s.insureClaimRepresentive || "" },
        ],
        DataType: "Item",
      },
      {
        RenderKeyName: "ApplicantInfo",
        ApplicationValue: [
          { isRender: true, Type: "Text", Name: "AIName",        Value: s.applicantAccidentName || "" },
          { isRender: true, Type: "Text", Name: "AIPhoneNo",     Value: s.applicantAccidentPhoneNumber || "" },
          { isRender: true, Type: "Text", Name: "AIHomePhNo",    Value: s.applicantAccidentHomePhoneNo || "" },
          { isRender: true, Type: "Text", Name: "AIBussPhNo",    Value: s.applicantAccidentBusinessPhoneNumber || "" },
          { isRender: true, Type: "Text", Name: "AISSN",         Value: s.applicantAccidentSSN || "" },
          { isRender: true, Type: "Text", Name: "AIDOB",         Value: formatDate(s.applicantAccidentDOB) },
          { isRender: true, Type: "Text", Name: "AIDOA",         Value: formatDate(s.applicantAccidentDOA) },
          { isRender: true, Type: "Text", Name: "AIAddress",     Value: s.applicantAccidentAddress || "" },
          { isRender: true, Type: "Text", Name: "AIAccPlaceInfo",Value: s.applicantAccidentAddress2 || "" },
          { isRender: true, Type: "Text", Name: "AIAccDesc",     Value: s.applicantAccidentDesc || "" },
          { isRender: true, Type: "Text", Name: "AIInjuryDesc",  Value: s.applicantAccidentDescInjury || "" },
          { isRender: true, Type: "Text", Name: "AISignDate",    Value: formatDate(s.signDate) },
        ],
        DataType: "Item",
      },
      {
        RenderKeyName: "SignInfo",
        ApplicationValue: [
          { isRender: true, Type: "Sign", Name: "AISign", Value: s.patientSignImg || "" },
        ],
        AssociatedRenderKeys: ["AISign1", "AISign2"],
        DataType: "Item",
      },
      {
        RenderKeyName: "VehicleInfo",
        ApplicationValue: [
          { isRender: true, Type: "Text",  Name: "VIOwnerName",      Value: s.ownerName || "" },
          { isRender: true, Type: "Text",  Name: "VIMake",           Value: s.make || "" },
          { isRender: true, Type: "Text",  Name: "VIYear",           Value: s.year || "" },
          { isRender: true, Type: "Radio", Name: "VITypeBus",        Value: s.vehicleType === "bus" },
          { isRender: true, Type: "Radio", Name: "VITypeTruck",      Value: s.vehicleType === "truck" },
          { isRender: true, Type: "Radio", Name: "VITypeAutomobile", Value: s.vehicleType === "auto" },
          { isRender: true, Type: "Radio", Name: "VITypeMotorcycle", Value: s.vehicleType === "motorcycle" },
          { isRender: true, Type: "Radio", Name: "VIDriverYes",      Value: s.isDriver === true },
          { isRender: true, Type: "Radio", Name: "VIDriverNo",       Value: s.isDriver === false },
          { isRender: true, Type: "Radio", Name: "VIPassengerYes",   Value: s.isPassenger === true },
          { isRender: true, Type: "Radio", Name: "VIPassengerNo",    Value: s.isPassenger === false },
          { isRender: true, Type: "Radio", Name: "VIPedestrianYes",  Value: s.isPedestrian === true },
          { isRender: true, Type: "Radio", Name: "VIPedestrianNo",   Value: s.isPedestrian === false },
          { isRender: true, Type: "Radio", Name: "VIPolicyMbrYes",   Value: s.isPolicyHolderHouseHold === true },
          { isRender: true, Type: "Radio", Name: "VIPolicyMbrNo",    Value: s.isPolicyHolderHouseHold === false },
          { isRender: true, Type: "Radio", Name: "VIRelativeYes",    Value: s.isOwnMotorVechile === true },
          { isRender: true, Type: "Radio", Name: "VIRelativeNo",     Value: s.isOwnMotorVechile === false },
        ],
        DataType: "Item",
      },
      {
        RenderKeyName: "TreatmentInfo",
        ApplicationValue: [
          { isRender: true, Type: "Radio", Name: "TIByDoctorYes",       Value: s.treatedByDoctorOrOtherPerson === true },
          { isRender: true, Type: "Radio", Name: "TIByDoctorNo",        Value: s.treatedByDoctorOrOtherPerson === false },
          { isRender: true, Type: "Text",  Name: "TIDoctorInfo",        Value: s.nameAndAddressOfDoctor || "" },
          { isRender: true, Type: "Radio", Name: "TIOutPatientYes",     Value: s.hospitalType === "out" },
          { isRender: true, Type: "Radio", Name: "TIInPatientYes",      Value: s.hospitalType === "in" },
          { isRender: true, Type: "Text",  Name: "TIHospitalInfo",      Value: s.hospitalNameAndAddress || "" },
          { isRender: true, Type: "Text",  Name: "TIAddmitDate",        Value: formatDate(s.DOA) },
          { isRender: true, Type: "Text",  Name: "TIBillAmtToDate",     Value: s.amountOfHealthBillToDate || "" },
          { isRender: true, Type: "Radio", Name: "TIMoreTreatmentYes",  Value: s.willHaveMoreTreatment === true },
          { isRender: true, Type: "Radio", Name: "TIMoreTreatmentNo",   Value: s.willHaveMoreTreatment === false },
          { isRender: true, Type: "Radio", Name: "TICourseEmpYes",      Value: s.inCourseOfEmployment === true },
          { isRender: true, Type: "Radio", Name: "TICourseEmpNo",       Value: s.inCourseOfEmployment === false },
        ],
        DataType: "Item",
      },
      {
        RenderKeyName: "WorkInfo",
        ApplicationValue: [
          { isRender: true, Type: "Text",  Name: "WILoseTime",        Value: s.lostTimeFromWork || "" },
          { isRender: true, Type: "Text",  Name: "WIAbsenceDate",     Value: formatDate(s.dateAbsenceFromWorkBegan) },
          { isRender: true, Type: "Text",  Name: "WIReturnWork",      Value: s.returnToWork || "" },
          { isRender: true, Type: "Text",  Name: "WIReturnDate",      Value: formatDate(s.dateReturnToWork) },
          { isRender: true, Type: "Text",  Name: "WITimeLost",        Value: s.timeLostFromWork || "" },
          { isRender: true, Type: "Text",  Name: "WIWeeklyEarning",   Value: s.grossAvgWeeklyEarning || "" },
          { isRender: true, Type: "Text",  Name: "WINoDayWeek",       Value: s.workPerWeek || "" },
          { isRender: true, Type: "Text",  Name: "WINoHrsDay",        Value: s.workPerDay || "" },
          { isRender: true, Type: "Radio", Name: "WIUnempBenefitYes", Value: s.receivingUnemploymentBenefit === true },
          { isRender: true, Type: "Radio", Name: "WIUnempBenefitNo",  Value: s.receivingUnemploymentBenefit === false },
        ],
        DataType: "Item",
      },
      {
        RenderKeyName: "EmployeerInfo",
        ApplicationValue: [
          { isRender: true, Type: "Text", Name: "EI1Address",    Value: s.employerAndAddress1 || "" },
          { isRender: true, Type: "Text", Name: "EI1Occupation", Value: s.occupation1 || "" },
          { isRender: true, Type: "Text", Name: "EI1FromDate",   Value: formatDate(s.fromDate1) },
          { isRender: true, Type: "Text", Name: "EI1ToDate",     Value: formatDate(s.toDate1) },
          { isRender: true, Type: "Text", Name: "EI2Address",    Value: s.employerAndAddress2 || "" },
          { isRender: true, Type: "Text", Name: "EI2Occupation", Value: s.occupation2 || "" },
          { isRender: true, Type: "Text", Name: "EI2FromDate",   Value: formatDate(s.fromDate2) },
          { isRender: true, Type: "Text", Name: "EI2ToDate",     Value: formatDate(s.toDate2) },
          { isRender: true, Type: "Text", Name: "EI3Address",    Value: s.employerAndAddress3 || "" },
          { isRender: true, Type: "Text", Name: "EI3Occupation", Value: s.occupation3 || "" },
          { isRender: true, Type: "Text", Name: "EI3FromDate",   Value: formatDate(s.fromDate3) },
          { isRender: true, Type: "Text", Name: "EI3ToDate",     Value: formatDate(s.toDate3) },
        ],
        DataType: "Item",
      },
      {
        RenderKeyName: "OtherInfo",
        ApplicationValue: [
          { isRender: true, Type: "Radio", Name: "OIOtherExpYes",  Value: s.hasOtherExpenses === true },
          { isRender: true, Type: "Radio", Name: "OIOtherExpNo",   Value: s.hasOtherExpenses === false },
          { isRender: true, Type: "Radio", Name: "OINYStDisabYes", Value: s.newYorkDisability === true },
          { isRender: true, Type: "Radio", Name: "OINYStDisabNo",  Value: s.newYorkDisability === false },
          { isRender: true, Type: "Radio", Name: "OIWCYes",        Value: s.workerCompensation === true },
          { isRender: true, Type: "Radio", Name: "OIWCNo",         Value: s.workerCompensation === false },
          { isRender: true, Type: "Text",  Name: "OIFormDate",     Value: formatDate(s.formSubmissionDate) },
        ],
        DataType: "Item",
      },
    ];
  };

  // ─── Save ─────────────────────────────────────────────────────────────────────
  const saveFunction = async () => {
    try {
      const notesdata = buildNotesData(); // reads from stateRef — always fresh
      const payload = {
        CaseFormDataId: null,
        CaseId: tokenData?.caseId ?? 0,
        CompanyId: tokenData?.companyId ?? "",
        UserId: tokenData?.userId ?? "US000000000000012172",
        DoctorId: tokenData?.doctorId ?? "",
        OfficeId: tokenData?.providerId ?? "",
        OldCaseFormDataId: 0,
        IsCopySign: false,
        ProviderSign: "",
        IsChanged: false,
        SavePdfInDocMgr: false,
        FormId: 4,
        FormName: "NF 2",
        TemplateName: "",
        EntityLevel: 0,
        FormType: "",
        IsFormCompleted: false,
        JsonData: JSON.stringify({ notesdata }),
        FileName: "",
        FilePath: "",
      };
      const response = await SaveCaseFormData(payload);
      if (!response?.HasException) {
        toast.success("NF2 saved successfully.");
        return true;
      } else {
        toast.error("Error while saving NF2.");
        return false;
      }
    } catch (error) {
      console.error("Error While Saving Patient Data:", error);
      return false;
    }
  };

  // ─── JSX ──────────────────────────────────────────────────────────────────────
  return (
    <>
      {loading ? (
        <SpinnerLoader message="Loading form data…" />
      ) : (
        <div className="nf2__wrapper">
          <div className="nf2__title-block">
            <div className="nf2__title-main">NF 2</div>
            <div className="nf2__title-sub">
              New York Motor Vehicle No-Fault Insurance Application for Benefits
            </div>
          </div>

          {/* 1. Insurer & Policy */}
          <SectionBadge num="1" title="Insurer & Policy Information" first />
          <div className="nf2__grid-4" style={{ marginTop: 8 }}>
            <div className="nf2__field nf2__field--span2">
              <label className="nf2__label"><b>Name of Insurer</b></label>
              <input className="nf2__input" type="text" value={insurerName}
                onChange={(e) => setInsurerName(e.target.value)} disabled />
            </div>
            <div className="nf2__field nf2__field--span2">
              <label className="nf2__label"><b>Address of Insurer</b></label>
              <textarea className="nf2__textarea" style={{ height: 64 }} value={insureAddress}
                onChange={(e) => setInsureAddress(e.target.value)} disabled />
            </div>
            <div className="nf2__field nf2__field--span2">
              <label className="nf2__label"><b>Policy Holder</b></label>
              <input className="nf2__input" type="text" value={policyHolder}
                onChange={(e) => setPolicyHolder(e.target.value)} disabled />
            </div>
            <div className="nf2__field nf2__field--span2">
              <label className="nf2__label"><b>Policy Number</b></label>
              <input className="nf2__input" type="text" value={policyNumber}
                onChange={(e) => setPolicyNumber(e.target.value)} disabled />
            </div>
            <div className="nf2__field nf2__field--span2">
              <label className="nf2__label"><b>Claim Number</b></label>
              <input className="nf2__input" type="text" value={claimNumber}
                onChange={(e) => setClaimNumber(e.target.value)} disabled />
            </div>
            <div className="nf2__field nf2__field--span2">
              <label className="nf2__label"><b>Name, Address & Phone of Insurer's Claim Representative</b></label>
              <textarea className="nf2__textarea" style={{ height: 64 }} value={insureClaimRepresentive}
                onChange={(e) => setInsureClaimRepresentive(e.target.value)} />
            </div>
          </div>

          {/* Legal Notice */}
          <div className="nf2__legal-panel" style={{ marginTop: 14, marginBottom: 8 }}>
            <p style={{ margin: 0 }}>
              TO ENABLE US TO DETERMINE IF YOU ARE ENTITLED TO BENEFITS UNDER THE NEW YORK
              NO-FAULT LAW, PLEASE COMPLETE THIS FORM AND RETURN IT PROMPTLY.
            </p>
            <p style={{ margin: "6px 0 0" }}>
              <b>IMPORTANT:</b> 1. TO BE ELIGIBLE FOR BENEFITS YOU MUST COMPLETE AND SIGN
              THIS APPLICATION. 2. YOU MUST SIGN ANY ATTACHED AUTHORIZATION(S). 3. RETURN
              PROMPTLY WITH COPIES OF ANY BILLS YOU HAVE RECEIVED TO DATE.
            </p>
          </div>

          {/* 2. Applicant and Accident Information */}
          <SectionBadge num="2" title="Applicant and Accident Information" />
          <div className="nf2__grid-4" style={{ marginTop: 8 }}>
            <div className="nf2__field nf2__field--span4">
              <label className="nf2__label"><b>Your Name</b></label>
              <input className="nf2__input" type="text" value={applicantAccidentName}
                onChange={(e) => setApplicantAccidentName(e.target.value)} disabled />
            </div>
            <div className="nf2__field nf2__field--span2">
              <label className="nf2__label"><b>Home Phone Number</b></label>
              <input className="nf2__input" type="tel" value={applicantAccidentHomePhoneNo}
                onChange={(e) => setApplicantAccidentHomePhoneNo(e.target.value)}
                placeholder="(999) 999-9999" />
            </div>
            <div className="nf2__field nf2__field--span2">
              <label className="nf2__label"><b>Phone Number</b></label>
              <input className="nf2__input" type="tel" value={applicantAccidentPhoneNumber}
                onChange={(e) => setApplicantAccidentPhoneNumber(e.target.value)}
                placeholder="(999) 999-9999" disabled />
            </div>
            <div className="nf2__field nf2__field--span2">
              <label className="nf2__label"><b>Business Phone Number</b></label>
              <input className="nf2__input" type="tel" value={applicantAccidentBusinessPhoneNumber}
                onChange={(e) => setApplicantAccidentBusinessPhoneNumber(e.target.value)}
                placeholder="(999) 999-9999" />
            </div>
            <div className="nf2__field nf2__field--span2">
              <label className="nf2__label"><b>Date of Birth</b></label>
              <DatePicker selected={applicantAccidentDOB} onChange={setApplicantAccidentDOB}
                dateFormat="MM/dd/yyyy" className="nf2__datepicker"
                showYearDropdown showMonthDropdown dropdownMode="select" disabled />
            </div>
            <div className="nf2__field nf2__field--span2">
              <label className="nf2__label"><b>Social Security Number</b></label>
              <input className="nf2__input" type="text" value={applicantAccidentSSN}
                onChange={(e) => setApplicantAccidentSSN(e.target.value)} disabled />
            </div>
            <div className="nf2__field nf2__field--span2">
              <label className="nf2__label"><b>Date and Time of Accident</b></label>
              <DatePicker selected={applicantAccidentDOA} onChange={setApplicantAccidentDOA}
                dateFormat="MM/dd/yyyy h:mm aa" className="nf2__datepicker"
                showTimeSelect showYearDropdown showMonthDropdown dropdownMode="select" />
            </div>
            <div className="nf2__field nf2__field--span2">
              <label className="nf2__label"><b>Address</b></label>
              <input className="nf2__input" type="text" value={applicantAccidentAddress}
                onChange={(e) => setApplicantAccidentAddress(e.target.value)} disabled />
            </div>
            <div className="nf2__field nf2__field--span4">
              <label className="nf2__label"><b>Place of Accident (street, city or town and state)</b></label>
              <input className="nf2__input" type="text" value={applicantAccidentAddress2}
                onChange={(e) => setApplicantAccidentAddress2(e.target.value)} />
            </div>
            <div className="nf2__field nf2__field--span4">
              <label className="nf2__label"><b>Brief Description of Accident</b></label>
              <input className="nf2__input" type="text" value={applicantAccidentDesc}
                onChange={(e) => setApplicantAccidentDesc(e.target.value)} />
            </div>
            <div className="nf2__field nf2__field--span4">
              <label className="nf2__label"><b>Describe Your Injury</b></label>
              <input className="nf2__input" type="text" value={applicantAccidentDescInjury}
                onChange={(e) => setApplicantAccidentDescInjury(e.target.value)} />
            </div>
          </div>

          {/* 3. Vehicle Information */}
          <SectionBadge num="3" title="Identity of Vehicle You Occupied or Operated at the Time of the Accident" />
          <div className="nf2__grid-4" style={{ marginTop: 8 }}>
            <div className="nf2__field nf2__field--span2">
              <label className="nf2__label"><b>Owner's Name</b></label>
              <input className="nf2__input" type="text" value={ownerName}
                onChange={(e) => setOwnerName(e.target.value)} />
            </div>
            <div className="nf2__field nf2__field--span1">
              <label className="nf2__label"><b>Make</b></label>
              <input className="nf2__input" type="text" value={make}
                onChange={(e) => setMake(e.target.value)} />
            </div>
            <div className="nf2__field nf2__field--span1">
              <label className="nf2__label"><b>Year</b></label>
              <input className="nf2__input" type="text" value={year}
                onChange={(e) => setYear(e.target.value)} maxLength={4} placeholder="YYYY" />
            </div>
            <div className="nf2__field nf2__field--span4">
              <label className="nf2__label"><b>This Vehicle Was</b></label>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 4 }}>
                <Radio checked={vehicleType === "bus"} value="bus" name="vehicleType"
                  label="A Bus or School Bus" onChange={(e) => setVehicleType(e.target.value)} />
                <Radio checked={vehicleType === "truck"} value="truck" name="vehicleType"
                  label="A Truck" onChange={(e) => setVehicleType(e.target.value)} />
                <Radio checked={vehicleType === "auto"} value="auto" name="vehicleType"
                  label="An Automobile" onChange={(e) => setVehicleType(e.target.value)} />
                <Radio checked={vehicleType === "motorcycle"} value="motorcycle" name="vehicleType"
                  label="A Motorcycle" onChange={(e) => setVehicleType(e.target.value)} />
              </div>
            </div>
            <div className="nf2__field nf2__field--span2">
              <label className="nf2__label"><b>Were you the Driver of the Motor Vehicle?</b></label>
              <div style={{ display: "flex", gap: 24, marginTop: 4 }}>
                <Radio checked={isDriver === true} value="yes" name="driverQuestion"
                  label="Yes" onChange={handleYesNo(setIsDriver)} />
                <Radio checked={isDriver === false} value="no" name="driverQuestion"
                  label="No" onChange={handleYesNo(setIsDriver)} />
              </div>
            </div>
            <div className="nf2__field nf2__field--span2">
              <label className="nf2__label"><b>Were you a Passenger in the Motor Vehicle?</b></label>
              <div style={{ display: "flex", gap: 24, marginTop: 4 }}>
                <Radio checked={isPassenger === true} value="yes" name="isPassenger"
                  label="Yes" onChange={handleYesNo(setIsPassenger)} />
                <Radio checked={isPassenger === false} value="no" name="isPassenger"
                  label="No" onChange={handleYesNo(setIsPassenger)} />
              </div>
            </div>
            <div className="nf2__field nf2__field--span2">
              <label className="nf2__label"><b>Were you a Pedestrian?</b></label>
              <div style={{ display: "flex", gap: 24, marginTop: 4 }}>
                <Radio checked={isPedestrian === true} value="yes" name="isPedestrian"
                  label="Yes" onChange={handleYesNo(setIsPedestrian)} />
                <Radio checked={isPedestrian === false} value="no" name="isPedestrian"
                  label="No" onChange={handleYesNo(setIsPedestrian)} />
              </div>
            </div>
            <div className="nf2__field nf2__field--span2">
              <label className="nf2__label"><b>Were you a member of the PolicyHolder's Household?</b></label>
              <div style={{ display: "flex", gap: 24, marginTop: 4 }}>
                <Radio checked={isPolicyHolderHouseHold === true} value="yes" name="household"
                  label="Yes" onChange={handleYesNo(setIsPolicyHolderHouseHold)} />
                <Radio checked={isPolicyHolderHouseHold === false} value="no" name="household"
                  label="No" onChange={handleYesNo(setIsPolicyHolderHouseHold)} />
              </div>
            </div>
            <div className="nf2__field nf2__field--span4">
              <label className="nf2__label"><b>Do you or a Relative with whom you reside own a motor vehicle?</b></label>
              <div style={{ display: "flex", gap: 24, marginTop: 4 }}>
                <Radio checked={isOwnMotorVechile === true} value="yes" name="ownVehicle"
                  label="Yes" onChange={handleYesNo(setIsOwnMotorVechile)} />
                <Radio checked={isOwnMotorVechile === false} value="no" name="ownVehicle"
                  label="No" onChange={handleYesNo(setIsOwnMotorVechile)} />
              </div>
            </div>
          </div>

          {/* 4. Treatment Information */}
          <SectionBadge num="4" title="Treatment Information" />
          <div className="nf2__grid-4" style={{ marginTop: 8 }}>
            <div className="nf2__field nf2__field--span2">
              <label className="nf2__label">
                <b>Were you treated by a doctor(s) or other person(s) furnishing health services?</b>
              </label>
              <div style={{ display: "flex", gap: 24, marginTop: 4 }}>
                <Radio checked={treatedByDoctorOrOtherPerson === true} value="yes" name="treated"
                  label="Yes" onChange={handleYesNo(setTreatedByDoctorOrOtherPerson)} />
                <Radio checked={treatedByDoctorOrOtherPerson === false} value="no" name="treated"
                  label="No" onChange={handleYesNo(setTreatedByDoctorOrOtherPerson)} />
              </div>
            </div>
            <div className="nf2__field nf2__field--span2">
              <label className="nf2__label"><b>If Yes, Name and Address of such Doctor(s) or Person(s)</b></label>
              <textarea className="nf2__textarea" style={{ height: 64 }} value={nameAndAddressOfDoctor}
                onChange={(e) => setNameAndAddressOfDoctor(e.target.value)} />
            </div>
            <div className="nf2__field nf2__field--span2">
              <label className="nf2__label"><b>If you were treated at a Hospital, were you an:</b></label>
              <div style={{ display: "flex", gap: 32, marginTop: 8 }}>
                <Radio checked={hospitalType === "out"} value="out" name="hospitalPatientType"
                  label="Out Patient" onChange={(e) => setHospitalType(e.target.value)} />
                <Radio checked={hospitalType === "in"} value="in" name="hospitalPatientType"
                  label="In Patient" onChange={(e) => setHospitalType(e.target.value)} />
              </div>
            </div>
            <div className="nf2__field nf2__field--span2">
              <label className="nf2__label"><b>Date of Admission</b></label>
              <DatePicker selected={DOA} onChange={setDOA} dateFormat="MM/dd/yyyy"
                className="nf2__datepicker" showYearDropdown showMonthDropdown dropdownMode="select" />
            </div>
            <div className="nf2__field nf2__field--span4">
              <label className="nf2__label"><b>Hospital's Name and Address</b></label>
              <textarea className="nf2__textarea" style={{ height: 64 }} value={hospitalNameAndAddress}
                onChange={(e) => setHospitalNameAndAddress(e.target.value)} />
            </div>
            <div className="nf2__field nf2__field--span2">
              <label className="nf2__label"><b>Amount of Health Bill to Date</b></label>
              <input className="nf2__input" type="text" value={amountOfHealthBillToDate}
                onChange={(e) => setAmountOfHealthBillToDate(e.target.value)} placeholder="$ 0.00" />
            </div>
            <div className="nf2__field nf2__field--span2">
              <label className="nf2__label"><b>Will you have more Health Treatment?</b></label>
              <div style={{ display: "flex", gap: 24, marginTop: 4 }}>
                <Radio checked={willHaveMoreTreatment === true} value="yes" name="moreTreatment"
                  label="Yes" onChange={handleYesNo(setWillHaveMoreTreatment)} />
                <Radio checked={willHaveMoreTreatment === false} value="no" name="moreTreatment"
                  label="No" onChange={handleYesNo(setWillHaveMoreTreatment)} />
              </div>
            </div>
            <div className="nf2__field nf2__field--span4">
              <label className="nf2__label">
                <b>At the time of your accident were you in the course of your Employment?</b>
              </label>
              <div style={{ display: "flex", gap: 24, marginTop: 4 }}>
                <Radio checked={inCourseOfEmployment === true} value="yes" name="employment"
                  label="Yes" onChange={handleYesNo(setInCourseOfEmployment)} />
                <Radio checked={inCourseOfEmployment === false} value="no" name="employment"
                  label="No" onChange={handleYesNo(setInCourseOfEmployment)} />
              </div>
            </div>
          </div>

          {/* 5. Work Information */}
          <SectionBadge num="5" title="Work Information" />
          <div className="nf2__grid-4" style={{ marginTop: 8 }}>
            <div className="nf2__field nf2__field--span2">
              <label className="nf2__label"><b>Did you lose time from work?</b></label>
              <input className="nf2__input" type="text" value={lostTimeFromWork}
                onChange={(e) => setLostTimeFromWork(e.target.value)} />
            </div>
            <div className="nf2__field nf2__field--span2">
              <label className="nf2__label"><b>Date Absence from Work Began</b></label>
              <DatePicker selected={dateAbsenceFromWorkBegan} onChange={setDateAbsenceFromWorkBegan}
                dateFormat="MM/dd/yyyy" className="nf2__datepicker"
                showYearDropdown showMonthDropdown dropdownMode="select" />
            </div>
            <div className="nf2__field nf2__field--span2">
              <label className="nf2__label"><b>Have you returned to work?</b></label>
              <input className="nf2__input" type="text" value={returnToWork}
                onChange={(e) => setReturnToWork(e.target.value)} />
            </div>
            <div className="nf2__field nf2__field--span2">
              <label className="nf2__label"><b>If Yes, Date Return to Work</b></label>
              <DatePicker selected={dateReturnToWork} onChange={setDateReturnToWork}
                dateFormat="MM/dd/yyyy" className="nf2__datepicker"
                showYearDropdown showMonthDropdown dropdownMode="select" />
            </div>
            <div className="nf2__field nf2__field--span2">
              <label className="nf2__label"><b>Amount of Time Lost from Work</b></label>
              <input className="nf2__input" type="text" value={timeLostFromWork}
                onChange={(e) => setTimeLostFromWork(e.target.value)} />
            </div>
            <div className="nf2__field nf2__field--span2">
              <label className="nf2__label"><b>What are your Gross Average Weekly Earnings?</b></label>
              <input className="nf2__input" type="text" value={grossAvgWeeklyEarning}
                onChange={(e) => setGrossAvgWeeklyEarning(e.target.value)} placeholder="$ 0.00" />
            </div>
            <div className="nf2__field nf2__field--span2">
              <label className="nf2__label"><b>Number of Days you Work Per Week</b></label>
              <input className="nf2__input" type="text" value={workPerWeek}
                onChange={(e) => setWorkPerWeek(e.target.value)} />
            </div>
            <div className="nf2__field nf2__field--span2">
              <label className="nf2__label"><b>Number of Hours you Work Per Day</b></label>
              <input className="nf2__input" type="text" value={workPerDay}
                onChange={(e) => setWorkPerDay(e.target.value)} />
            </div>
            <div className="nf2__field nf2__field--span4">
              <label className="nf2__label">
                <b>Were you receiving Unemployment Benefits at the time of accident?</b>
              </label>
              <div style={{ display: "flex", gap: 24, marginTop: 4 }}>
                <Radio checked={receivingUnemploymentBenefit === true} value="yes" name="unemployment"
                  label="Yes" onChange={handleYesNo(setReceivingUnemploymentBenefit)} />
                <Radio checked={receivingUnemploymentBenefit === false} value="no" name="unemployment"
                  label="No" onChange={handleYesNo(setReceivingUnemploymentBenefit)} />
              </div>
            </div>
          </div>

          {/* 6. Employer Information */}
          <SectionBadge num="6" title="Employer Information" />
          <div className="nf2__employer-table">
            <div className="nf2__employer-header">
              <label className="nf2__label"><b>Employer and Address</b></label>
              <label className="nf2__label"><b>Occupation</b></label>
              <label className="nf2__label"><b>From</b></label>
              <label className="nf2__label"><b>To</b></label>
            </div>
            {[1, 2, 3].map((idx) => {
              const addrState = [employerAndAddress1, employerAndAddress2, employerAndAddress3][idx - 1];
              const setAddr  = [setEmployerAndAddress1, setEmployerAndAddress2, setEmployerAndAddress3][idx - 1];
              const occState = [occupation1, occupation2, occupation3][idx - 1];
              const setOcc   = [setOccupation1, setOccupation2, setOccupation3][idx - 1];
              const fromState = [fromDate1, fromDate2, fromDate3][idx - 1];
              const setFrom   = [setFromDate1, setFromDate2, setFromDate3][idx - 1];
              const toState  = [toDate1, toDate2, toDate3][idx - 1];
              const setTo    = [setToDate1, setToDate2, setToDate3][idx - 1];
              return (
                <div key={idx} className="nf2__employer-row">
                  <input className="nf2__input" type="text" value={addrState}
                    onChange={(e) => setAddr(e.target.value)} placeholder="Employer name and address" />
                  <input className="nf2__input" type="text" value={occState}
                    onChange={(e) => setOcc(e.target.value)} placeholder="Occupation" />
                  <DatePicker selected={fromState} onChange={setFrom} dateFormat="MM/dd/yyyy"
                    className="nf2__datepicker" showYearDropdown showMonthDropdown dropdownMode="select" />
                  <DatePicker selected={toState} onChange={setTo} dateFormat="MM/dd/yyyy"
                    className="nf2__datepicker" showYearDropdown showMonthDropdown dropdownMode="select" />
                </div>
              );
            })}
          </div>

          {/* 7. Other Benefits & Expenses */}
          <SectionBadge num="7" title="Other Benefits & Expenses" />
          <div className="nf2__grid-4" style={{ marginTop: 8 }}>
            <div className="nf2__field nf2__field--span2">
              <label className="nf2__label"><b>As a result of your injury have you had any other expense?</b></label>
              <div style={{ display: "flex", gap: 24, marginTop: 4 }}>
                <Radio checked={hasOtherExpenses === true} value="yes" name="otherExpense"
                  label="Yes" onChange={handleYesNo(setHasOtherExpenses)} />
                <Radio checked={hasOtherExpenses === false} value="no" name="otherExpense"
                  label="No" onChange={handleYesNo(setHasOtherExpenses)} />
              </div>
            </div>
            <div className="nf2__field nf2__field--span2">
              <label className="nf2__label" style={{ fontStyle: "italic" }}>
                If Yes, attach explanation and amounts of such expense.
              </label>
            </div>
            <div className="nf2__field nf2__field--span4">
              <label className="nf2__label">
                <b>Due to this accident have you received or are you eligible for payments under any of the following:</b>
              </label>
            </div>
            <div className="nf2__field nf2__field--span2">
              <label className="nf2__label"><b>New York State Disability</b></label>
              <div style={{ display: "flex", gap: 24, marginTop: 4 }}>
                <Radio checked={newYorkDisability === true} value="yes" name="nyDisability"
                  label="Yes" onChange={handleYesNo(setNewYorkDisability)} />
                <Radio checked={newYorkDisability === false} value="no" name="nyDisability"
                  label="No" onChange={handleYesNo(setNewYorkDisability)} />
              </div>
            </div>
            <div className="nf2__field nf2__field--span2">
              <label className="nf2__label"><b>Worker Compensation</b></label>
              <div style={{ display: "flex", gap: 24, marginTop: 4 }}>
                <Radio checked={workerCompensation === true} value="yes" name="workersComp"
                  label="Yes" onChange={handleYesNo(setWorkerCompensation)} />
                <Radio checked={workerCompensation === false} value="no" name="workersComp"
                  label="No" onChange={handleYesNo(setWorkerCompensation)} />
              </div>
            </div>
          </div>

          {/* Signature & Certification */}
          <div className="nf2__section" style={{ marginTop: 14 }}>
            <span className="nf2__section-title">Signature & Certification</span>
            <span className="nf2__section-line" />
          </div>
          <div className="nf2__grid-4" style={{ marginTop: 8 }}>
            <div className="nf2__field nf2__field--span2">
              <label className="nf2__label"><b>Signature</b></label>
              <div className="nf2__signature-pad" title="Click to sign" onClick={clickPatientSign}>
                {patientSignImg ? (
                  <img
                    src={patientSignImg.startsWith("data:") ? patientSignImg : `data:image/png;base64,${patientSignImg}`}
                    alt="Patient Signature"
                  />
                ) : (
                  <span>Click to sign</span>
                )}
              </div>
            </div>
            <div className="nf2__field nf2__field--span2">
              <div className="nf2__field">
                <label className="nf2__label"><b>Signature Date</b></label>
                <DatePicker selected={signDate} onChange={setSignDate} dateFormat="MM/dd/yyyy"
                  className="nf2__datepicker" showYearDropdown showMonthDropdown dropdownMode="select" />
              </div>
              <div className="nf2__field" style={{ marginTop: 12 }}>
                <label className="nf2__label"><b>Date of Form Submission</b></label>
                <DatePicker selected={formSubmissionDate} onChange={setFormSubmissionDate}
                  dateFormat="MM/dd/yyyy" className="nf2__datepicker"
                  showYearDropdown showMonthDropdown dropdownMode="select" />
              </div>
              <SignaturePad
                isOpen={sigPadOpen}
                title="Patient Signature"
                existingSign={normalizeSignatureDataUrl(patientSignImg)}
                onClose={() => setSigPadOpen(false)}
                onSave={(base64) => { signUpdatedCall(base64); setSigPadOpen(false); }}
                onClear={clearSignCall}
              />
            </div>
          </div>
        </div>
      )}
    </>
  );
});

export default NF2;